import {Alert, Grid, GridProps, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import AurButton from '../aur-button/AurButton';
import {compact, map, trim} from 'lodash';

interface IAurDragAndDropBox {
  FileUploadIcon?: React.ElementType;
  buttonLabel?: string;
  dragAndDropText?: string;
  buttonIcon?: React.ReactNode;
  isDragDrop?: boolean;
  buttonHint?: string;
  dataTestId?: string;
  orText?: boolean;
  dragAndDropProps?: GridProps;
  dragAndDropDescription?: React.ReactNode;
  limitFileSizeInBytes?: number;
  acceptFileType?: string;
  handleFile?: (files: File[],isFilesDropped:boolean) => void;
}

const AurDragAndDropBox = ({
  FileUploadIcon,
  dragAndDropText = '',
  dragAndDropDescription,
  orText,
  buttonLabel = '',
  buttonHint,
  buttonIcon,
  dataTestId,
  isDragDrop = true,
  dragAndDropProps,
  limitFileSizeInBytes,
  acceptFileType,
  handleFile,
}: IAurDragAndDropBox) => {
  const [validationError, setValidationError] = useState<string>();
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearError = useCallback(() => {
    setValidationError(undefined);
  }, []);

  const handleButtonClick = useCallback(() => {
    clearError();
    fileInputRef.current?.click();
  }, [clearError]);

  const handleFileValidation = useCallback(
    (files: File[], isFilesDropped:boolean) => {
      const allowedFileTypes = compact(
        map(acceptFileType?.split(',') ?? [], trim)
      );
      const hasTypeRestriction = allowedFileTypes.length > 0;
      const hasSizeRestriction = !!limitFileSizeInBytes;

      const limitCrossedFiles: File[] = [];
      const unsupportedFiles: File[] = [];
      const validFiles: File[] = [];

      const isTypeAllowed = (file: File) =>
        !hasTypeRestriction || allowedFileTypes.includes(file.type);
      const isSizeAllowed = (file: File) =>
        !hasSizeRestriction || file.size <= limitFileSizeInBytes;

      for (const file of files) {
        if (!isTypeAllowed(file)) {
          unsupportedFiles.push(file);
          continue;
        }
        if (!isSizeAllowed(file)) {
          limitCrossedFiles.push(file);
          continue;
        }
        validFiles.push(file);
      }

      if (unsupportedFiles.length > 0) {
        setValidationError(t('fileCatalyst.validationForFileType'));
      } else if (limitCrossedFiles.length > 0) {
        const filesName = limitCrossedFiles.map((file) => file.name).join(', ');
        setValidationError(
          t('fileCatalyst.validationForFileSizeLimit', {name: filesName})
        );
      }

      let result: File[] = [];

      if (validFiles.length > 0) {
        result = validFiles;
      } else if (hasSizeRestriction || hasTypeRestriction) {
        result = [];
      } else {
        result = files;
      }

      handleFile?.(result,isFilesDropped);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [acceptFileType, handleFile, limitFileSizeInBytes]
  );

  const onDragDropFileHandler = useCallback(
    (acceptedFiles: File[]) => {
      handleFileValidation(acceptedFiles,true);
    },
    [handleFileValidation]
  );

  const handleDragOver = (e?: any) => {
    e?.preventDefault(); // Required to allow dropping
    e.dataTransfer.dropEffect = 'copy'; // Influences the *system* cursor to show a 'copy' icon
  };

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop: onDragDropFileHandler,
    onDragOver: handleDragOver,
    multiple: true,
    disabled: !isDragDrop,
  });

  useEffect(() => {
    if (isDragActive) {
      clearError();
      document.body.classList.add('dragging-cursor-hidden');
    } else {
      document.body.classList.remove('dragging-cursor-hidden');
    }

    return () => {
      document.body.classList.remove('dragging-cursor-hidden');
    };
  }, [clearError, isDragActive]);

  return (
    <Grid
      container
      className={`drag-and-drop-container ${validationError ? 'file-error' : ''}`}
      {...(isDragDrop ? getRootProps() : {})}
    >
      {validationError && (
        <Alert
          data-testid={'error-message'}
          className="alert-validation-error-container"
          severity="error"
          aria-label={validationError}
          tabIndex={0}
        >
          {validationError}
        </Alert>
      )}
      <Grid
        item
        xs={12}
        md={12}
        className={`inner-drag-and-drop-grid dragging-${isDragActive}`}
        {...dragAndDropProps}
      >
        {FileUploadIcon && <FileUploadIcon />}
        <Typography className="typo-text headline3_size drag-text">
          {dragAndDropText}
        </Typography>
        {dragAndDropDescription}
        {orText && (
          <Typography className="body3_size mist-color-900 or-text">
            {t('overlay.orText')}
          </Typography>
        )}

        <AurButton
          label={buttonLabel}
          type="continue"
          onClick={handleButtonClick}
          startIcon={buttonIcon}
          classNames={`upload-btn typo-text buttonLarge_size`}
          ariaLabel={buttonHint}
          dataTestId={dataTestId}
        />

        <input
          {...getInputProps()}
          ref={fileInputRef}
          style={{display: 'none'}}
          data-testid="file-input"
          accept={acceptFileType}
          onChange={(e) => {
            if (e.target.files) {
              e.target.blur();
              handleFileValidation(Array.from(e.target.files),false);
            }
          }}
        />
      </Grid>
    </Grid>
  );
};

export default AurDragAndDropBox;
